import type { BingUetEventName, BingUetEventParams } from '#nuxt-scripts/registry/bing-uet';

export default defineNuxtPlugin({
  name: 'kygunco:uet',
  dependsOn: ['kygunco:bus'],
  parallel: true,
  setup: () => {
    const config = useRuntimeConfig().public.uet;
    const { $bus } = useNuxtApp();
    const enabled = config.enabled;

    const { proxy, load, onError } = useScriptBingUet({
      scriptOptions: {
        trigger: 'manual',
      },
    });

    /**
     * Since this script is not critical to the site, we'll just catch the exception and do nothing.
     * The API is stubbed, so it will be safe to use but will do nothing without the script.
     */
    onError(noop);

    const track = (event: BingUetEventName, payload?: BingUetEventParams) => payload === undefined
      ? proxy.uetq.push('event', event)
      : proxy.uetq.push('event', event, payload);

    $bus.once('app:interacted', () => enabled && load());

    $bus.on('product:view', (product, _variation) =>
      track('view_item', {
        ecomm_prodid: [product.id],
        ecomm_pagetype: 'product',
        ecomm_totalvalue: product.price,
        currency: 'USD',
        items: [
          {
            id: product.id,
            quantity: 1,
            price: product.price,
          },
        ],
      }),
    );

    $bus.on('product:add', (product, variation, quantity) =>
      track('add_to_cart', {
        ecomm_prodid: [product.id],
        ecomm_pagetype: 'cart',
        ecomm_totalvalue: (variation?.price ?? product.price) * (quantity ?? 1),
        currency: 'USD',
        items: [
          {
            id: product.id,
            quantity: quantity ?? 1,
            price: variation?.price ?? product.price,
          },
        ],
      }),
    );

    $bus.on('catalog:add', (product, _collection) =>
      track('add_to_cart', {
        ecomm_prodid: [product.id],
        ecomm_pagetype: 'cart',
        ecomm_totalvalue: product.price,
        currency: 'USD',
        items: [
          {
            id: product.id,
            quantity: 1,
            price: product.price,
          },
        ],
      }),
    );

    $bus.on('checkout:start', items =>
      track('begin_checkout', {
        ecomm_prodid: items.map(i => i.productId),
        ecomm_pagetype: 'purchase',
        ecomm_totalvalue: items.map(i => i.total).reduce((a, b) => a + b, 0),
        currency: 'USD',
        items: items.map(i => ({
          id: i.productId,
          quantity: i.quantity,
          price: i.price,
        })),
      }),
    );

    $bus.on('checkout:complete', (orders) => {
      for (const order of orders) {
        // Track the Purchase conversion goal
        track('1021436885/ngm8CICk_JQZENXHh-cD', {
          ecomm_prodid: order.items.map(i => i.productId),
          ecomm_pagetype: 'purchase',
          ecomm_totalvalue: order.grandTotal,
          revenue_value: order.grandTotal,
          currency: 'USD',
          transaction_id: order.id,
          items: order.items.map(i => ({
            id: i.productId,
            quantity: i.quantity,
            price: i.price,
          })),
        });

        // Track a generic purchase event for any other goals
        track('purchase', {
          ecomm_prodid: order.items.map(i => i.productId),
          ecomm_pagetype: 'purchase',
          ecomm_totalvalue: order.grandTotal,
          revenue_value: order.grandTotal,
          currency: 'USD',
          transaction_id: order.id,
          items: order.items.map(i => ({
            id: i.productId,
            quantity: i.quantity,
            price: i.price,
          })),
        });
      }
    });

    return {
      provide: {
        uet: {
          enabled,
          track,
          push: proxy.uetq.push,
        },
      },
    };
  },
});
